import { switchMap, take } from 'rxjs/operators';
import { Toaster } from '@densityco/ui';
import createRxStore, { actions, ofType, rxDispatch } from '..';
import {
  TriggerDataCaptureState,
  TriggerDataCaptureAction,
  TriggerDataCaptureActionTypes,
  TriggerDataCaptureStep,
  TriggerDataCaptureLength,
} from '../../types/trigger-data-capture';
import { Sensor } from '../../types/api';
import { SenseiApi } from '../../utils/api';
import { getRequiredSessionToken } from '../../utils/session-token';

import {
  DurationUnit,
  convertFromDurationToSeconds,
} from '../../types/durations';

export const initialState: TriggerDataCaptureState = {
  step: TriggerDataCaptureStep.INITIAL,
  open: false,
  formDisabled: false,
};

function reducer(
  state: TriggerDataCaptureState,
  action: TriggerDataCaptureAction,
): TriggerDataCaptureState {
  switch (action.type) {
    case TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_OPEN:
      if (action.openIntoRegularStep) {
        return {
          step: TriggerDataCaptureStep.REGULAR,
          open: true,
          formDisabled: false,
          captureLength: TriggerDataCaptureLength.SHORT,
          motion: false,
          motionDetectionTriggerLine: false,
        };
      } else {
        return {
          step: TriggerDataCaptureStep.INITIAL,
          open: true,
          formDisabled: false,
        };
      }
    case TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CLOSE:
      return { ...state, open: false };

    case TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_LOADING:
      return { ...state, formDisabled: true };
    case TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_COMPLETE:
      return { ...state, formDisabled: true, open: false };
    case TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_ERROR:
      return { ...state, formDisabled: false };

    case TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_STEP:
      if (action.step === TriggerDataCaptureStep.REGULAR) {
        return {
          open: state.open,
          formDisabled: false,
          step: TriggerDataCaptureStep.REGULAR,
          captureLength: TriggerDataCaptureLength.SHORT,
          motion: false,
          motionDetectionTriggerLine: false,
        };
      }

      if (action.step === TriggerDataCaptureStep.DENSE) {
        return {
          open: state.open,
          formDisabled: false,
          step: TriggerDataCaptureStep.DENSE,
          captureLength: TriggerDataCaptureLength.SHORT,
          numberOfCaptures: '300',
          maxCaptureDuration: '5',
          maxCaptureDurationUnit: DurationUnit.DAYS,
          motion: true,
          motionDetectionTriggerLine: true,
          advancedControlsVisible: false,
        };
      }

      return {
        open: state.open,
        formDisabled: false,
        step: TriggerDataCaptureStep.INITIAL,
      };

    case TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_SHOW_ADVANCED_CONTROLS:
      if (state.step !== TriggerDataCaptureStep.DENSE) {
        return state;
      }
      return { ...state, advancedControlsVisible: true };

    case TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_MOTION:
      if (
        state.step !== TriggerDataCaptureStep.REGULAR &&
        state.step !== TriggerDataCaptureStep.DENSE
      ) {
        return state;
      }
      return {
        ...state,
        motion: action.checked,
        // Reset "motionDetectionTriggerLine" back to false when motion is unset
        motionDetectionTriggerLine: action.checked
          ? state.motionDetectionTriggerLine
          : false,
      };

    case TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_MOTION_DETECTION_TRIGGER_LINE:
      if (
        state.step !== TriggerDataCaptureStep.REGULAR &&
        state.step !== TriggerDataCaptureStep.DENSE
      ) {
        return state;
      }
      return { ...state, motionDetectionTriggerLine: action.checked };

    case TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_CAPTURE_LENGTH:
      if (
        state.step !== TriggerDataCaptureStep.REGULAR &&
        state.step !== TriggerDataCaptureStep.DENSE
      ) {
        return state;
      }
      // When changing to a custom capture duration, then add in the custom capture duration field.
      if (action.value === TriggerDataCaptureLength.CUSTOM) {
        return {
          ...state,
          captureLength: TriggerDataCaptureLength.CUSTOM,
          customCaptureLengthInSeconds: '',
        };
      }
      return { ...state, captureLength: action.value };

    case TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_CUSTOM_CAPTURE_LENGTH:
      if (
        state.step !== TriggerDataCaptureStep.REGULAR &&
        state.step !== TriggerDataCaptureStep.DENSE
      ) {
        return state;
      }
      if (state.captureLength !== TriggerDataCaptureLength.CUSTOM) {
        return state;
      }
      return { ...state, customCaptureLengthInSeconds: action.value };

    case TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_MAX_CAPTURE_DURATION:
      if (state.step !== TriggerDataCaptureStep.DENSE) {
        return state;
      }
      return { ...state, maxCaptureDuration: action.value };

    case TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_MAX_CAPTURE_DURATION_UNIT:
      if (state.step !== TriggerDataCaptureStep.DENSE) {
        return state;
      }
      return { ...state, maxCaptureDurationUnit: action.value };

    case TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_CHANGE_NUM_CAPTURES:
      if (state.step !== TriggerDataCaptureStep.DENSE) {
        return state;
      }
      return { ...state, numberOfCaptures: action.value };

    default:
      return state;
  }
}

const TriggerDataCaptureStore = createRxStore(
  'TriggerDataCaptureStore',
  initialState,
  reducer,
);

// ---------------------------------------------------------------------------
// SIDE EFFECT: When submitting the trigger data capture process, make a
// request to sensei api.
// ----------------------------------------------------------------------------
actions
  .pipe(
    ofType<{
      serialNumbers: Array<Sensor['serial_number']>;
      onComplete: () => void;
    }>(TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_SUBMIT),
    switchMap(
      () => TriggerDataCaptureStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    if (state.step === TriggerDataCaptureStep.INITIAL) {
      return;
    }

    const capturesSuffix = action.serialNumbers.length > 1 ? 's' : '';
    const durationSeconds = (() => {
      switch (state.captureLength) {
        case TriggerDataCaptureLength.SHORT:
          return 60 /* seconds */;
        case TriggerDataCaptureLength.LONG:
          return 180 /* seconds */;
        default:
          return parseInt(state.customCaptureLengthInSeconds, 10);
      }
    })();

    rxDispatch({
      type: TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_LOADING,
    });

    let numberOfCaptures: number | undefined;
    let maxCaptureDuration: number | undefined;
    if (state.step === TriggerDataCaptureStep.DENSE) {
      numberOfCaptures = parseInt(state.numberOfCaptures, 10);
      if (isNaN(numberOfCaptures)) {
        return;
      }
      maxCaptureDuration = convertFromDurationToSeconds(
        parseInt(state.maxCaptureDuration, 10),
        state.maxCaptureDurationUnit,
      );
      if (isNaN(maxCaptureDuration)) {
        return;
      }
    }

    const token = getRequiredSessionToken();
    SenseiApi.triggerCapture(token, action.serialNumbers, {
      duration: durationSeconds,
      motion: state.motion,
      motion_detection_trigger_line: state.motionDetectionTriggerLine,
      dense_capture: state.step === TriggerDataCaptureStep.DENSE,
      number_of_captures: numberOfCaptures,
      max_capture_duration: maxCaptureDuration,
    })
      .then(() => {
        rxDispatch({
          type: TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_COMPLETE,
        });
        Toaster.showToast({
          text: `Capture${capturesSuffix} triggered.`,
        });
        if (action.onComplete) {
          action.onComplete();
        }
      })
      .catch((err) => {
        rxDispatch({
          type: TriggerDataCaptureActionTypes.TRIGGER_DATA_CAPTURE_ERROR,
        });
        Toaster.showToast({
          type: 'error',
          text: `Error triggering capture${capturesSuffix}.`,
        });
      });
  });

export default TriggerDataCaptureStore;
